import { opToSymbol } from 'conditions/utils/utils'
import lodash from 'lodash'
import { SystemConditionDef } from './condition'
import { ConditionComparison, ConditionEvalRecord, SystemContext } from './types'
import { CheckOperator, CheckScopeDef } from './utils'

export type { ConditionComparison, ConditionEvalRecord } from './types'

function parseFilterKey(key: string): { prop: string; op: CheckOperator } {
  const parts = key.split('__')
  if (parts.length === 1) {
    return { prop: parts[0], op: 'eq' }
  }
  const op = parts[parts.length - 1] as CheckOperator
  return { prop: parts.slice(0, -1).join('__'), op }
}

export function scopeComparison(scope: unknown, def: CheckScopeDef): ConditionComparison {
  const op = def.op || 'eq'
  return {
    field: def.prop,
    operator: opToSymbol(op),
    expected: def.value,
    actual: lodash.get(scope, def.prop),
  }
}

export function filterComparisons(scope: unknown, filter: Record<string, unknown>): ConditionComparison[] {
  return Object.keys(filter || {}).map((key) => {
    const { prop, op } = parseFilterKey(key)
    return {
      field: prop,
      operator: opToSymbol(op),
      expected: filter[key],
      actual: lodash.get(scope, prop),
    }
  })
}

export function appendScopeComparison(context: SystemContext, scope: unknown, def: CheckScopeDef): void {
  if (context.pendingConditionComparisons === undefined) return
  context.pendingConditionComparisons.push(scopeComparison(scope, def))
}

export function appendFilterComparisons(context: SystemContext, scope: unknown, filter: Record<string, unknown>): void {
  if (context.pendingConditionComparisons === undefined) return
  context.pendingConditionComparisons.push(...filterComparisons(scope, filter))
}

export function compactConditionDef(def: SystemConditionDef): Record<string, unknown> {
  const copy = { ...def } as Record<string, unknown>
  delete copy.conditions
  return copy
}

export function formatConditionFailureReport(failures: ConditionEvalRecord[]): string {
  if (!failures.length) {
    return 'No failing condition details captured (try localStorage.setItem("debugDesignRulesVerbose", "true"))'
  }
  return failures
    .map((f) => {
      const lines = [`  • ${f.path} → ${f.type}${f.negated ? ' (negated)' : ''}`]
      if (f.note) {
        lines.push(`      ${f.note}`)
      }
      if (f.comparisons?.length) {
        for (const c of f.comparisons) {
          lines.push(
            `      ${c.field} ${c.operator} ${JSON.stringify(c.expected)}  (actual: ${JSON.stringify(c.actual)})`
          )
        }
      }
      if (f.conditionDef) {
        lines.push(`      conditionDef: ${JSON.stringify(f.conditionDef)}`)
      }
      return lines.join('\n')
    })
    .join('\n')
}
